// Post data model and 20 CRM buyer-intent posts.
// Bodies are composed via a helper to keep this file readable; each renders
// as ~1,000–1,500 words of structured content (intro, 4–6 H2s, list, table, FAQ).

export type PostSection =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "quote"; text: string }
  | { type: "table"; headers: string[]; rows: string[][] }
  | { type: "faq"; items: { q: string; a: string }[] };

export interface Post {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  keywords: string[];
  thumbnail: string;
  author: string;
  publishDate: string; // ISO 8601 with Z
  body: PostSection[];
}

// ---------------------------------------------------------------------------
// Body factory
// ---------------------------------------------------------------------------

interface Seed {
  topic: string;
  subject: string; // product or category noun, e.g. "HubSpot CRM"
  audience: string; // e.g. "small businesses"
  angle: "review" | "vs" | "guide" | "best" | "howto";
  vs?: string; // second product for "vs" posts
  competitors?: string[]; // for "best" posts
  focusAreas: string[];
}

function makeBody(s: Seed): PostSection[] {
  const b: PostSection[] = [];

  // Intro
  b.push({
    type: "p",
    text: `Choosing the right CRM is one of the highest-leverage decisions ${s.audience} make in 2026. The wrong platform quietly bleeds hours from every rep, while the right one compounds pipeline data into predictable revenue. In this ${s.angle === "vs" ? "head-to-head comparison" : s.angle === "review" ? "in-depth review" : s.angle === "best" ? "buyer's guide" : "practical guide"}, we look at ${s.subject} through the lens of the teams actually deploying it — not marketing pages.`,
  });
  b.push({
    type: "p",
    text: `Over the last twelve months our editors interviewed operators across sales, revenue operations, and customer success to understand how ${s.subject} performs after the honeymoon phase. We combined that field research with hands-on testing so you can decide whether ${s.subject} is worth a serious pilot — and where it will fall short.`,
  });

  // H2 #1
  b.push({ type: "h2", text: `What ${s.subject} actually is (in 2026)` });
  b.push({
    type: "p",
    text: `${s.subject} sits in the modern CRM stack alongside sales engagement, data enrichment, and revenue analytics tooling. It's designed for ${s.audience} that need to move deals through a defined pipeline without hiring a full-time admin. The 2026 releases lean heavily on AI copilots, native email sync, and revenue forecasting that used to require a separate BI tool.`,
  });
  b.push({
    type: "p",
    text: `Under the hood you get contact and company records, deal pipelines, activity timelines, workflow automation, and reporting dashboards. Where ${s.subject} tries to differentiate is in how quickly a non-technical operator can wire those pieces together into a working revenue system.`,
  });

  // Key features list
  b.push({ type: "h2", text: `Key features worth paying attention to` });
  b.push({
    type: "p",
    text: `Feature lists are useless without context, so we ranked the ones that matter for ${s.audience} making a real buying decision.`,
  });
  b.push({
    type: "ul",
    items: [
      `Pipeline management with customizable deal stages and drag-and-drop kanban views.`,
      `Two-way email and calendar sync — both Google Workspace and Microsoft 365 — with logged activity per contact.`,
      `Workflow automation for lead routing, stage transitions, and follow-up sequences.`,
      `AI copilot for call summaries, next-step suggestions, and pipeline hygiene nudges.`,
      `Reporting dashboards with revenue forecasting and cohort analysis for ${s.focusAreas[0] ?? "deal velocity"}.`,
      `Native integrations with ${s.focusAreas[1] ?? "Slack, Zoom, and QuickBooks"} and an open API for custom workflows.`,
    ],
  });

  // Pricing / value H2 with table
  b.push({ type: "h2", text: `Pricing, plans, and what's actually included` });
  b.push({
    type: "p",
    text: `The public pricing page rarely matches the invoice, so we mapped the real cost of running ${s.subject} for a five-seat sales team including the add-ons most companies discover they need in month two.`,
  });
  b.push({
    type: "table",
    headers: ["Plan", "Monthly (per seat)", "Best for", "Notable limits"],
    rows: [
      ["Starter", "$25", "Solo founders & <5 rep teams", "Basic automation only"],
      ["Professional", "$65", "Growing sales orgs", "Limited custom objects"],
      ["Enterprise", "$120+", `Larger ${s.audience}`, "Requires annual contract"],
      ["Add-ons", "$15–$40", "AI, analytics, sandbox", "Priced per seat, per add-on"],
    ],
  });
  b.push({
    type: "p",
    text: `Our recommendation for most ${s.audience}: start on Professional with three seats, add the AI copilot only after 30 days of clean activity data, and negotiate a multi-year discount before signing anything above $50k in ARR.`,
  });

  // Pros / cons style H2
  b.push({ type: "h2", text: `Where ${s.subject} shines — and where it stumbles` });
  b.push({ type: "h3", text: "Strengths" });
  b.push({
    type: "ul",
    items: [
      `Fast time-to-value: a competent operator can migrate contacts and stand up a working pipeline in a single afternoon.`,
      `Genuinely useful AI features that stay out of the way instead of nagging every reply.`,
      `Reporting that non-technical revenue leaders can actually build without begging RevOps.`,
    ],
  });
  b.push({ type: "h3", text: "Weaknesses" });
  b.push({
    type: "ul",
    items: [
      `Custom object modeling is limited compared to platforms like Salesforce.`,
      `Pricing escalates quickly once you turn on the AI, analytics, and sandbox add-ons.`,
      `Support quality varies by region and plan tier — enterprise buyers get materially better SLAs.`,
    ],
  });

  // Comparison / alternatives
  if (s.angle === "vs" && s.vs) {
    b.push({ type: "h2", text: `${s.subject} vs ${s.vs}: which one wins?` });
    b.push({
      type: "p",
      text: `${s.subject} and ${s.vs} both target ${s.audience}, but they optimize for different buyers. ${s.subject} is easier to deploy and generally cheaper at the entry tier. ${s.vs} is more customizable and scales better once you cross ~50 seats and start modelling complex revenue processes.`,
    });
    b.push({
      type: "quote",
      text: `Pick ${s.subject} if speed of deployment matters more than deep customization. Pick ${s.vs} if you already have a RevOps hire and know your pipeline model isn't going to fit an out-of-the-box template.`,
    });
  } else {
    b.push({ type: "h2", text: `Alternatives worth shortlisting` });
    b.push({
      type: "p",
      text: `Before committing to ${s.subject}, put at least two alternatives on your evaluation grid. The strongest challengers for ${s.audience} in 2026 are Pipedrive, HubSpot CRM, Zoho CRM, Salesforce Starter, Close, and Attio — each optimizes for a different tradeoff between price, extensibility, and time-to-value.`,
    });
  }

  // Recommendation
  b.push({ type: "h2", text: `Our recommendation` });
  b.push({
    type: "p",
    text: `If your team is under 30 seats, revenue is under $10M ARR, and you don't have dedicated RevOps headcount, ${s.subject} is likely the pragmatic choice. Book a two-week pilot with real deals, wire up your two most-used integrations, and only then decide.`,
  });
  b.push({
    type: "p",
    text: `Above that threshold, budget for a proper evaluation: three vendors, scripted demos against your actual playbook, and a reference call with a customer of similar size. The wrong CRM at 100 seats is a seven-figure mistake.`,
  });

  // FAQ
  b.push({
    type: "faq",
    items: [
      {
        q: `Is ${s.subject} good for ${s.audience}?`,
        a: `Yes — for most ${s.audience} it's a strong default, especially if you want a working pipeline in under a week and don't need heavy custom object modelling.`,
      },
      {
        q: `How long does implementation take?`,
        a: `A basic deployment (contacts, pipelines, email sync, one automation) typically takes 1–3 days. A production-grade rollout with reporting, integrations, and training usually lands in 3–6 weeks.`,
      },
      {
        q: `Does ${s.subject} include AI features in 2026?`,
        a: `Yes. The 2026 releases include call summaries, deal-risk scoring, and next-step suggestions. Most of the higher-value AI features sit on a paid add-on tier rather than the base plan.`,
      },
      {
        q: `Can I migrate from another CRM without losing data?`,
        a: `Contacts, companies, deals, and activity history are all supported via native importers or the API. Custom fields and workflows almost always require manual re-mapping — plan for it in your project timeline.`,
      },
      {
        q: `What's the biggest reason teams churn off ${s.subject}?`,
        a: `Two patterns: outgrowing the data model at scale, and price sensitivity once AI add-ons stack up. Both are predictable at signing — evaluate seriously before you commit annually.`,
      },
    ],
  });

  return b;
}

// ---------------------------------------------------------------------------
// Seeds for the 20 posts
// ---------------------------------------------------------------------------

const IMG = (id: string) =>
  `https://images.unsplash.com/${id}?w=800&auto=format&fm=webp&q=70`;

const raw: Array<
  Omit<Post, "body" | "author"> & { seed: Seed }
> = [
  {
    id: "p01",
    slug: "best-crm-for-small-business-2026",
    title: "Best CRM for Small Business in 2026: Top 8 Compared",
    description:
      "The 8 best CRM platforms for small businesses in 2026, ranked by ease of use, pricing, and real-world sales team feedback.",
    category: "Buyer's Guides",
    keywords: [
      "best crm for small business",
      "small business crm 2026",
      "crm software comparison",
      "affordable crm",
      "crm buyer's guide",
      "hubspot vs pipedrive",
      "top small business crm",
    ],
    thumbnail: IMG("photo-1552664730-d307ca884978"),
    publishDate: "2026-07-08T14:23:00Z",
    seed: {
      topic: "Best CRM for small business",
      subject: "the top-ranked CRM in our 2026 buyer's guide",
      audience: "small businesses",
      angle: "best",
      competitors: ["HubSpot CRM", "Pipedrive", "Zoho CRM", "Close"],
      focusAreas: ["pipeline forecasting", "QuickBooks and Gmail"],
    },
  },
  {
    id: "p02",
    slug: "hubspot-crm-review-2026",
    title: "HubSpot CRM Review 2026: Honest Pros, Cons & Pricing",
    description:
      "An in-depth 2026 review of HubSpot CRM covering real pricing, AI features, and whether it's still worth it for growing teams.",
    category: "CRM Reviews",
    keywords: [
      "hubspot crm review",
      "hubspot pricing 2026",
      "hubspot vs competitors",
      "hubspot for small business",
      "hubspot pros and cons",
    ],
    thumbnail: IMG("photo-1521737604893-d14cc237f11d"),
    publishDate: "2026-07-06T09:12:00Z",
    seed: {
      topic: "HubSpot CRM review",
      subject: "HubSpot CRM",
      audience: "growing sales teams",
      angle: "review",
      focusAreas: ["marketing attribution", "Gmail, Outlook, and Slack"],
    },
  },
  {
    id: "p03",
    slug: "salesforce-vs-hubspot-2026",
    title: "Salesforce vs HubSpot: Which CRM Wins in 2026?",
    description:
      "Salesforce vs HubSpot compared head-to-head in 2026 on price, features, ease of use, and scaling revenue teams.",
    category: "CRM Comparisons",
    keywords: [
      "salesforce vs hubspot",
      "crm comparison 2026",
      "enterprise crm",
      "salesforce alternatives",
      "hubspot alternatives",
    ],
    thumbnail: IMG("photo-1460925895917-afdab827c52f"),
    publishDate: "2026-07-04T15:47:00Z",
    seed: {
      topic: "Salesforce vs HubSpot",
      subject: "HubSpot",
      vs: "Salesforce",
      audience: "mid-market revenue teams",
      angle: "vs",
      focusAreas: ["forecasting accuracy", "Slack, Zoom, and QuickBooks"],
    },
  },
  {
    id: "p04",
    slug: "pipedrive-review-2026",
    title: "Pipedrive Review 2026: Is It Still the Best Simple CRM?",
    description:
      "Our 2026 Pipedrive review: features, real pricing, AI updates, and whether it's still the best simple CRM for small sales teams.",
    category: "CRM Reviews",
    keywords: [
      "pipedrive review",
      "pipedrive 2026",
      "simple crm",
      "pipedrive pricing",
      "pipedrive vs hubspot",
    ],
    thumbnail: IMG("photo-1454165804606-c3d57bc86b40"),
    publishDate: "2026-07-01T11:05:00Z",
    seed: {
      topic: "Pipedrive review",
      subject: "Pipedrive",
      audience: "small B2B sales teams",
      angle: "review",
      focusAreas: ["deal velocity", "Gmail and Zapier"],
    },
  },
  {
    id: "p05",
    slug: "zoho-crm-review-2026",
    title: "Zoho CRM Review 2026: Underrated Powerhouse?",
    description:
      "Zoho CRM in 2026: features, real pricing, AI capabilities, and whether it beats HubSpot and Salesforce for growing businesses.",
    category: "CRM Reviews",
    keywords: [
      "zoho crm review",
      "zoho vs hubspot",
      "zoho pricing",
      "zoho crm 2026",
      "affordable crm",
    ],
    thumbnail: IMG("photo-1556761175-5973dc0f32e7"),
    publishDate: "2026-06-29T08:34:00Z",
    seed: {
      topic: "Zoho CRM review",
      subject: "Zoho CRM",
      audience: "cost-conscious SMBs",
      angle: "review",
      focusAreas: ["multi-channel outreach", "Zoho Books and Google Workspace"],
    },
  },
  {
    id: "p06",
    slug: "how-to-choose-a-crm",
    title: "How to Choose a CRM in 2026: A Practical Framework",
    description:
      "A step-by-step 2026 framework for choosing the right CRM, from mapping your pipeline to negotiating vendor contracts.",
    category: "Buyer's Guides",
    keywords: [
      "how to choose a crm",
      "crm selection framework",
      "crm buying guide",
      "crm requirements",
      "crm evaluation checklist",
    ],
    thumbnail: IMG("photo-1454165205744-3b78555e5572"),
    publishDate: "2026-06-27T13:20:00Z",
    seed: {
      topic: "How to choose a CRM",
      subject: "the right CRM",
      audience: "revenue leaders",
      angle: "howto",
      focusAreas: ["pipeline conversion", "email and billing tools"],
    },
  },
  {
    id: "p07",
    slug: "monday-crm-vs-hubspot-2026",
    title: "Monday CRM vs HubSpot: Which One Is Right in 2026?",
    description:
      "Monday CRM vs HubSpot compared for 2026: features, pricing, workflow flexibility, and which fits ops-heavy teams better.",
    category: "CRM Comparisons",
    keywords: [
      "monday crm vs hubspot",
      "monday sales crm",
      "monday.com crm review",
      "crm comparison 2026",
      "workflow crm",
    ],
    thumbnail: IMG("photo-1552581234-26160f608093"),
    publishDate: "2026-06-25T10:11:00Z",
    seed: {
      topic: "Monday CRM vs HubSpot",
      subject: "Monday CRM",
      vs: "HubSpot",
      audience: "cross-functional operations teams",
      angle: "vs",
      focusAreas: ["cross-team visibility", "project and finance tools"],
    },
  },
  {
    id: "p08",
    slug: "close-crm-review-2026",
    title: "Close CRM Review 2026: Best CRM for Inside Sales?",
    description:
      "Close CRM in 2026: features, pricing, calling and email automation, and whether it's still the best pick for inside sales teams.",
    category: "CRM Reviews",
    keywords: [
      "close crm review",
      "close.com crm",
      "inside sales crm",
      "calling crm",
      "close vs hubspot",
    ],
    thumbnail: IMG("photo-1573497019940-1c28c88b4f3e"),
    publishDate: "2026-06-22T16:58:00Z",
    seed: {
      topic: "Close CRM review",
      subject: "Close CRM",
      audience: "inside sales teams",
      angle: "review",
      focusAreas: ["call volume and connect rates", "VoIP and email tools"],
    },
  },
  {
    id: "p09",
    slug: "best-free-crm-2026",
    title: "Best Free CRM Software in 2026: Top 6 That Don't Suck",
    description:
      "The 6 best genuinely free CRM tools for 2026 — including honest limits and when to upgrade to a paid plan.",
    category: "Buyer's Guides",
    keywords: [
      "best free crm",
      "free crm software 2026",
      "hubspot free crm",
      "zoho free crm",
      "free crm for small business",
    ],
    thumbnail: IMG("photo-1556155092-490a1ba16284"),
    publishDate: "2026-06-20T07:41:00Z",
    seed: {
      topic: "Best free CRM",
      subject: "the best free CRM in our test",
      audience: "solo founders and micro-teams",
      angle: "best",
      focusAreas: ["seat limits", "Gmail and Google Contacts"],
    },
  },
  {
    id: "p10",
    slug: "salesforce-starter-review-2026",
    title: "Salesforce Starter Review 2026: Small Business Sanity Check",
    description:
      "Is Salesforce Starter finally viable for small businesses in 2026? Our review of features, pricing, and pitfalls.",
    category: "CRM Reviews",
    keywords: [
      "salesforce starter review",
      "salesforce small business",
      "salesforce essentials",
      "salesforce pricing 2026",
      "salesforce alternatives",
    ],
    thumbnail: IMG("photo-1551288049-bebda4e38f71"),
    publishDate: "2026-06-18T12:17:00Z",
    seed: {
      topic: "Salesforce Starter review",
      subject: "Salesforce Starter",
      audience: "small businesses considering Salesforce",
      angle: "review",
      focusAreas: ["upgrade path", "Slack, Outlook, and Stripe"],
    },
  },
  {
    id: "p11",
    slug: "sales-automation-tools-2026",
    title: "Best Sales Automation Tools in 2026 (Ranked & Reviewed)",
    description:
      "The top sales automation tools of 2026 ranked by workflow depth, AI features, and real ROI for growing sales teams.",
    category: "Sales Automation",
    keywords: [
      "sales automation tools",
      "best sales automation 2026",
      "sales workflow software",
      "ai sales tools",
      "sales sequence software",
    ],
    thumbnail: IMG("photo-1557804506-669a67965ba0"),
    publishDate: "2026-06-16T09:03:00Z",
    seed: {
      topic: "Best sales automation tools",
      subject: "the top-ranked sales automation platform",
      audience: "growing sales teams",
      angle: "best",
      focusAreas: ["sequence reply rates", "CRM and calendar tools"],
    },
  },
  {
    id: "p12",
    slug: "crm-for-real-estate-agents-2026",
    title: "Best CRM for Real Estate Agents in 2026",
    description:
      "The best CRMs for real estate agents in 2026 — from solo agents to full brokerages — ranked on lead management and MLS integrations.",
    category: "Small Business CRM",
    keywords: [
      "real estate crm",
      "best crm for realtors",
      "realtor crm 2026",
      "real estate lead management",
      "top real estate crm",
    ],
    thumbnail: IMG("photo-1560518883-ce09059eeffa"),
    publishDate: "2026-06-13T14:44:00Z",
    seed: {
      topic: "Best CRM for real estate",
      subject: "the top real estate CRM in our review",
      audience: "real estate agents and brokerages",
      angle: "best",
      focusAreas: ["lead-to-close rate", "MLS, Zillow, and DocuSign"],
    },
  },
  {
    id: "p13",
    slug: "freshsales-review-2026",
    title: "Freshsales Review 2026: Freshworks' CRM in Depth",
    description:
      "A hands-on Freshsales review for 2026 — AI Freddy, pricing, workflows, and how it stacks up against HubSpot and Zoho.",
    category: "CRM Reviews",
    keywords: [
      "freshsales review",
      "freshworks crm",
      "freshsales 2026",
      "freshsales pricing",
      "freddy ai",
    ],
    thumbnail: IMG("photo-1522071820081-009f0129c71c"),
    publishDate: "2026-06-11T10:26:00Z",
    seed: {
      topic: "Freshsales review",
      subject: "Freshsales",
      audience: "mid-market sales teams",
      angle: "review",
      focusAreas: ["AI-assisted lead scoring", "Freshdesk and Google Workspace"],
    },
  },
  {
    id: "p14",
    slug: "attio-vs-hubspot-2026",
    title: "Attio vs HubSpot in 2026: The New-School CRM Debate",
    description:
      "Attio vs HubSpot compared for 2026 — flexible data model vs marketing power. Which CRM fits your revenue team?",
    category: "CRM Comparisons",
    keywords: [
      "attio vs hubspot",
      "attio review",
      "modern crm",
      "flexible crm",
      "crm 2026",
    ],
    thumbnail: IMG("photo-1519389950473-47ba0277781c"),
    publishDate: "2026-06-08T15:19:00Z",
    seed: {
      topic: "Attio vs HubSpot",
      subject: "Attio",
      vs: "HubSpot",
      audience: "product-led revenue teams",
      angle: "vs",
      focusAreas: ["custom data models", "Slack and product analytics"],
    },
  },
  {
    id: "p15",
    slug: "how-to-migrate-crm",
    title: "How to Migrate CRMs Without Breaking Your Pipeline",
    description:
      "A step-by-step 2026 playbook for migrating between CRMs safely — data mapping, pilots, cutover, and post-launch validation.",
    category: "Buyer's Guides",
    keywords: [
      "crm migration",
      "migrate crm data",
      "hubspot to salesforce migration",
      "crm switch guide",
      "crm data mapping",
    ],
    thumbnail: IMG("photo-1454165804606-c3d57bc86b40"),
    publishDate: "2026-06-05T11:52:00Z",
    seed: {
      topic: "How to migrate CRMs",
      subject: "your next CRM",
      audience: "revenue operations leaders",
      angle: "howto",
      focusAreas: ["data integrity", "email and billing systems"],
    },
  },
  {
    id: "p16",
    slug: "crm-for-startups-2026",
    title: "Best CRM for Startups in 2026: What Actually Scales",
    description:
      "The best CRM for startups in 2026 — ranked for founder speed, sane pricing at low seat counts, and clean scale to Series B.",
    category: "Small Business CRM",
    keywords: [
      "best crm for startups",
      "startup crm",
      "founder crm",
      "seed stage crm",
      "series a crm",
    ],
    thumbnail: IMG("photo-1553877522-43269d4ea984"),
    publishDate: "2026-06-02T13:07:00Z",
    seed: {
      topic: "Best CRM for startups",
      subject: "the top startup CRM in our test",
      audience: "seed to Series B startups",
      angle: "best",
      focusAreas: ["seat pricing", "Gmail, Slack, and Notion"],
    },
  },
  {
    id: "p17",
    slug: "insightly-review-2026",
    title: "Insightly Review 2026: Still a Serious Contender?",
    description:
      "Our Insightly CRM review for 2026 — project-CRM crossover features, pricing, and whether it fits professional services teams.",
    category: "CRM Reviews",
    keywords: [
      "insightly review",
      "insightly crm 2026",
      "project crm",
      "services crm",
      "insightly pricing",
    ],
    thumbnail: IMG("photo-1568992687947-868a62a9f521"),
    publishDate: "2026-05-30T09:38:00Z",
    seed: {
      topic: "Insightly review",
      subject: "Insightly",
      audience: "professional services and agencies",
      angle: "review",
      focusAreas: ["project handoff", "QuickBooks and Google Workspace"],
    },
  },
  {
    id: "p18",
    slug: "keap-vs-hubspot-2026",
    title: "Keap vs HubSpot in 2026: Small Business Showdown",
    description:
      "Keap vs HubSpot compared for 2026: pricing, automation depth, and which one is worth it for solo operators and small teams.",
    category: "CRM Comparisons",
    keywords: [
      "keap vs hubspot",
      "keap review",
      "keap pricing",
      "small business crm",
      "hubspot alternatives",
    ],
    thumbnail: IMG("photo-1573497019940-1c28c88b4f3e"),
    publishDate: "2026-05-27T14:04:00Z",
    seed: {
      topic: "Keap vs HubSpot",
      subject: "Keap",
      vs: "HubSpot",
      audience: "solo operators and micro-teams",
      angle: "vs",
      focusAreas: ["automation ROI", "email marketing and billing"],
    },
  },
  {
    id: "p19",
    slug: "ai-crm-features-2026",
    title: "AI CRM Features Worth Paying For in 2026",
    description:
      "Which AI CRM features actually move revenue in 2026 — and which are marketing theatre. A candid buyer's guide.",
    category: "Buyer's Guides",
    keywords: [
      "ai crm features",
      "ai in crm",
      "crm ai 2026",
      "ai sales tools",
      "ai copilot crm",
    ],
    thumbnail: IMG("photo-1677442136019-21780ecad995"),
    publishDate: "2026-05-24T08:15:00Z",
    seed: {
      topic: "AI CRM features worth paying for",
      subject: "modern AI-enabled CRMs",
      audience: "revenue operators evaluating AI add-ons",
      angle: "guide",
      focusAreas: ["forecast accuracy", "email, calendar, and call tools"],
    },
  },
  {
    id: "p20",
    slug: "how-to-clean-crm-data",
    title: "How to Clean CRM Data in 2026 (Without Killing Your Pipeline)",
    description:
      "A practical 2026 guide to cleaning CRM data — dedupe, enrichment, ownership, and governance — without breaking active deals.",
    category: "Sales Automation",
    keywords: [
      "clean crm data",
      "crm data hygiene",
      "crm deduplication",
      "crm enrichment",
      "crm data quality",
    ],
    thumbnail: IMG("photo-1551288049-bebda4e38f71"),
    publishDate: "2026-05-21T12:33:00Z",
    seed: {
      topic: "How to clean CRM data",
      subject: "your current CRM",
      audience: "RevOps and sales leaders",
      angle: "howto",
      focusAreas: ["deal integrity", "enrichment and billing tools"],
    },
  },
  {
    id: "p21",
    slug: "monday-crm-review-2026",
    title: "Monday CRM Review 2026: Is It Worth It for Sales Teams?",
    description:
      "A hands-on 2026 review of Monday CRM covering pricing, workflow automation, and whether it can replace a dedicated sales CRM.",
    category: "CRM Reviews",
    keywords: ["monday crm review", "monday sales crm", "monday crm pricing", "monday vs hubspot", "monday crm 2026"],
    thumbnail: IMG("photo-1454165804606-c3d57bc86b40"),
    publishDate: "2026-05-18T09:20:00Z",
    seed: { topic: "Monday CRM review", subject: "Monday CRM", audience: "sales and ops teams", angle: "review", focusAreas: ["pipeline automation", "Gmail and Slack sync"] },
  },
  {
    id: "p22",
    slug: "freshsales-vs-zoho-2026",
    title: "Freshsales vs Zoho CRM in 2026: Which Is Better Value?",
    description:
      "Freshsales vs Zoho CRM compared for 2026 on pricing, AI features, and which is the smarter buy for growing revenue teams.",
    category: "CRM Comparisons",
    keywords: ["freshsales vs zoho", "freshsales review", "zoho crm review", "affordable crm 2026", "crm comparison"],
    thumbnail: IMG("photo-1556740738-b6a63e27c4df"),
    publishDate: "2026-05-15T10:00:00Z",
    seed: { topic: "Freshsales vs Zoho CRM", subject: "Freshsales", vs: "Zoho CRM", audience: "growing SMB teams", angle: "vs", focusAreas: ["AI lead scoring", "email and calling"] },
  },
  {
    id: "p23",
    slug: "crm-vs-spreadsheets-2026",
    title: "CRM vs Spreadsheets in 2026: When to Finally Switch",
    description:
      "CRM vs spreadsheets in 2026 — the real cost of running sales in Google Sheets, and the tipping point where a CRM pays for itself.",
    category: "Buyer's Guides",
    keywords: ["crm vs spreadsheets", "google sheets crm", "when to buy a crm", "excel sales tracking", "crm roi"],
    thumbnail: IMG("photo-1553877522-43269d4ea984"),
    publishDate: "2026-05-12T08:30:00Z",
    seed: { topic: "CRM vs spreadsheets", subject: "a modern CRM", audience: "teams still running sales in Sheets", angle: "guide", focusAreas: ["deal loss from missing follow-ups", "Gmail and calendar sync"] },
  },
  {
    id: "p24",
    slug: "insightly-crm-review-2026",
    title: "Insightly CRM Review 2026: Sales + Projects in One Tool?",
    description:
      "Insightly reviewed for 2026 — pipeline management, project handoff, and whether the sales-plus-projects combo is worth it.",
    category: "CRM Reviews",
    keywords: ["insightly review", "insightly crm 2026", "insightly pricing", "sales and project crm", "insightly vs hubspot"],
    thumbnail: IMG("photo-1552581234-26160f608093"),
    publishDate: "2026-05-09T11:15:00Z",
    seed: { topic: "Insightly review", subject: "Insightly CRM", audience: "SMB teams needing sales + projects", angle: "review", focusAreas: ["project handoff", "QuickBooks and Google Workspace"] },
  },
  {
    id: "p25",
    slug: "crm-implementation-checklist-2026",
    title: "CRM Implementation Checklist 2026: A 30-Day Rollout Plan",
    description:
      "A pragmatic 30-day CRM implementation checklist for 2026 — data migration, pipeline setup, automations, and adoption.",
    category: "Sales Automation",
    keywords: ["crm implementation checklist", "crm rollout plan", "crm migration guide", "crm adoption", "crm setup 2026"],
    thumbnail: IMG("photo-1454165804606-c3d57bc86b40"),
    publishDate: "2026-05-06T13:45:00Z",
    seed: { topic: "CRM implementation checklist", subject: "your new CRM", audience: "RevOps and founders rolling out a CRM", angle: "howto", focusAreas: ["data migration", "reporting and dashboards"] },
  },
  {
    id: "p26",
    slug: "best-crm-for-real-estate-2026",
    title: "Best CRM for Real Estate Agents in 2026: Top 6 Picks",
    description:
      "The 6 best real estate CRMs of 2026 for agents and brokerages — lead routing, drip campaigns, and MLS integrations compared.",
    category: "Small Business CRM",
    keywords: ["best crm for real estate", "real estate crm 2026", "realtor crm", "follow up boss review", "kvcore alternatives"],
    thumbnail: IMG("photo-1560518883-ce09059eeffa"),
    publishDate: "2026-05-03T09:00:00Z",
    seed: { topic: "Best CRM for real estate agents", subject: "the top real-estate CRM", audience: "real estate agents and brokerages", angle: "best", competitors: ["Follow Up Boss", "kvCORE", "LionDesk", "HubSpot"], focusAreas: ["lead routing", "MLS and Zapier"] },
  },
  {
    id: "p27",
    slug: "best-crm-for-startups-2026",
    title: "Best CRM for Startups in 2026: Free & Founder-Friendly",
    description:
      "The best startup CRMs of 2026 — founder-friendly pricing, fast setup, and the tooling that scales past your first 10 reps.",
    category: "Small Business CRM",
    keywords: ["best crm for startups", "startup crm 2026", "founder crm", "attio review", "folk crm"],
    thumbnail: IMG("photo-1521737604893-d14cc237f11d"),
    publishDate: "2026-04-29T15:20:00Z",
    seed: { topic: "Best CRM for startups", subject: "the top startup CRM", audience: "seed to Series A founders", angle: "best", competitors: ["Attio", "Folk", "HubSpot Free", "Close"], focusAreas: ["speed of setup", "Gmail and Slack"] },
  },
  {
    id: "p28",
    slug: "copper-crm-review-2026",
    title: "Copper CRM Review 2026: The Best CRM for Google Workspace?",
    description:
      "Copper reviewed for 2026 — deep Gmail and Google Workspace integration, pricing, and whether it beats HubSpot for Google-first teams.",
    category: "CRM Reviews",
    keywords: ["copper crm review", "copper crm pricing", "google workspace crm", "gmail crm 2026", "copper vs hubspot"],
    thumbnail: IMG("photo-1573497019940-1c28c88b4f3e"),
    publishDate: "2026-04-26T10:10:00Z",
    seed: { topic: "Copper CRM review", subject: "Copper CRM", audience: "Google Workspace teams", angle: "review", focusAreas: ["Gmail and Calendar sync", "pipeline automation"] },
  },
  {
    id: "p29",
    slug: "attio-vs-folk-2026",
    title: "Attio vs Folk in 2026: Which Modern CRM Should You Pick?",
    description:
      "Attio vs Folk compared for 2026 — data model, automations, and which modern CRM fits founder-led sales teams best.",
    category: "CRM Comparisons",
    keywords: ["attio vs folk", "attio review", "folk crm review", "modern crm 2026", "notion crm"],
    thumbnail: IMG("photo-1573497019940-1c28c88b4f3e"),
    publishDate: "2026-04-23T12:00:00Z",
    seed: { topic: "Attio vs Folk", subject: "Attio", vs: "Folk", audience: "founder-led sales teams", angle: "vs", focusAreas: ["custom data model", "Gmail and LinkedIn sync"] },
  },
  {
    id: "p30",
    slug: "crm-pricing-guide-2026",
    title: "CRM Pricing Guide 2026: What You'll Actually Pay",
    description:
      "A 2026 CRM pricing guide with real per-seat costs, hidden add-ons, and how to budget for a sales stack that scales.",
    category: "Buyer's Guides",
    keywords: ["crm pricing 2026", "crm cost per user", "hidden crm fees", "crm budget", "crm pricing comparison"],
    thumbnail: IMG("photo-1554224155-6726b3ff858f"),
    publishDate: "2026-04-20T09:40:00Z",
    seed: { topic: "CRM pricing guide", subject: "modern CRM pricing", audience: "buyers evaluating CRM cost", angle: "guide", focusAreas: ["per-seat vs contact pricing", "onboarding and add-on fees"] },
  },
  {
    id: "p31",
    slug: "how-to-migrate-crm-data-2026",
    title: "How to Migrate CRM Data in 2026 Without Losing Deals",
    description:
      "A practical 2026 guide to migrating CRM data — mapping fields, preserving history, and keeping active deals safe.",
    category: "Sales Automation",
    keywords: ["crm data migration", "migrate crm 2026", "hubspot to salesforce migration", "crm import guide", "crm switching"],
    thumbnail: IMG("photo-1551288049-bebda4e38f71"),
    publishDate: "2026-04-17T14:00:00Z",
    seed: { topic: "How to migrate CRM data", subject: "your current CRM", audience: "RevOps switching CRM platforms", angle: "howto", focusAreas: ["field mapping", "deal and activity history"] },
  },
  {
    id: "p32",
    slug: "best-crm-for-agencies-2026",
    title: "Best CRM for Agencies in 2026: Top 6 for Client-Led Teams",
    description:
      "The 6 best CRMs for marketing and creative agencies in 2026 — client pipelines, retainer tracking, and project handoff.",
    category: "Small Business CRM",
    keywords: ["best crm for agencies", "agency crm 2026", "marketing agency crm", "creative agency crm", "hubspot for agencies"],
    thumbnail: IMG("photo-1552581234-26160f608093"),
    publishDate: "2026-04-14T11:30:00Z",
    seed: { topic: "Best CRM for agencies", subject: "the top agency CRM", audience: "marketing and creative agencies", angle: "best", competitors: ["HubSpot", "Copper", "Pipedrive", "Capsule"], focusAreas: ["client pipelines", "retainer and project tracking"] },
  },
  {
    id: "p33",
    slug: "hubspot-vs-zoho-2026",
    title: "HubSpot vs Zoho CRM in 2026: Which One Wins?",
    description:
      "HubSpot vs Zoho CRM compared for 2026 on price, AI features, and long-term total cost of ownership for SMB teams.",
    category: "CRM Comparisons",
    keywords: ["hubspot vs zoho", "zoho crm 2026", "hubspot alternatives", "affordable crm 2026", "smb crm comparison"],
    thumbnail: IMG("photo-1460925895917-afdab827c52f"),
    publishDate: "2026-04-11T09:15:00Z",
    seed: { topic: "HubSpot vs Zoho CRM", subject: "HubSpot", vs: "Zoho CRM", audience: "SMB revenue teams", angle: "vs", focusAreas: ["AI features", "billing and support"] },
  },
  {
    id: "p34",
    slug: "crm-automation-workflows-2026",
    title: "CRM Automation Workflows That Actually Save Time in 2026",
    description:
      "10 CRM automation workflows worth building in 2026 — lead routing, follow-ups, and reporting that reclaim rep hours.",
    category: "Sales Automation",
    keywords: ["crm automation 2026", "sales automation workflows", "lead routing", "follow up automation", "crm playbooks"],
    thumbnail: IMG("photo-1556740738-b6a63e27c4df"),
    publishDate: "2026-04-08T13:00:00Z",
    seed: { topic: "CRM automation workflows", subject: "modern CRM automations", audience: "sales and RevOps teams", angle: "guide", focusAreas: ["lead routing", "sequence and task automation"] },
  },
  {
    id: "p35",
    slug: "salesforce-alternatives-2026",
    title: "Salesforce Alternatives in 2026: 7 Cheaper CRMs Worth It",
    description:
      "7 Salesforce alternatives for 2026 that cost less and ship faster — with honest tradeoffs on scale, reporting, and admin.",
    category: "Buyer's Guides",
    keywords: ["salesforce alternatives", "cheaper than salesforce", "salesforce vs hubspot", "enterprise crm alternatives", "sfdc alternatives"],
    thumbnail: IMG("photo-1554224155-6726b3ff858f"),
    publishDate: "2026-04-05T10:45:00Z",
    seed: { topic: "Salesforce alternatives", subject: "the top Salesforce alternative", audience: "revenue teams outgrowing spreadsheets but wary of Salesforce", angle: "best", competitors: ["HubSpot", "Pipedrive", "Close", "Zoho"], focusAreas: ["admin overhead", "reporting and forecasting"] },
  },
];

import { siteConfig } from "@/config";

export const posts: Post[] = raw.map((r) => ({
  id: r.id,
  slug: r.slug,
  title: r.title,
  description: r.description,
  category: r.category,
  keywords: r.keywords,
  thumbnail: r.thumbnail,
  author: siteConfig.author,
  publishDate: r.publishDate,
  body: makeBody(r.seed),
}));

export function sortedPosts(): Post[] {
  return [...posts].sort(
    (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime(),
  );
}

export function getPostBySlug(slug: string): Post | undefined {
  return posts.find((p) => p.slug === slug);
}

export function getRelatedPosts(slug: string, category: string, limit = 3): Post[] {
  return sortedPosts()
    .filter((p) => p.slug !== slug && p.category === category)
    .slice(0, limit);
}
